// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { ReachabilityAnalysisRuns } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  ReachabilityAnalysisRun,
  ReachabilityAnalysisRunsListNextOptionalParams,
  ReachabilityAnalysisRunsListOptionalParams,
  ReachabilityAnalysisRunsListResponse,
  ReachabilityAnalysisRunsGetOptionalParams,
  ReachabilityAnalysisRunsGetResponse,
  ReachabilityAnalysisRunsCreateOptionalParams,
  ReachabilityAnalysisRunsCreateResponse,
  ReachabilityAnalysisRunsDeleteOptionalParams,
  ReachabilityAnalysisRunsDeleteResponse,
  ReachabilityAnalysisRunsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing ReachabilityAnalysisRuns operations. */
export class ReachabilityAnalysisRunsImpl implements ReachabilityAnalysisRuns {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class ReachabilityAnalysisRuns class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets list of Reachability Analysis Runs.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    options?: ReachabilityAnalysisRunsListOptionalParams,
  ): PagedAsyncIterableIterator<ReachabilityAnalysisRun> {
    const iter = this.listPagingAll(resourceGroupName, networkManagerName, workspaceName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(
          resourceGroupName,
          networkManagerName,
          workspaceName,
          options,
          settings,
        );
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    options?: ReachabilityAnalysisRunsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<ReachabilityAnalysisRun[]> {
    let result: ReachabilityAnalysisRunsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, networkManagerName, workspaceName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        resourceGroupName,
        networkManagerName,
        workspaceName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    options?: ReachabilityAnalysisRunsListOptionalParams,
  ): AsyncIterableIterator<ReachabilityAnalysisRun> {
    for await (const page of this.listPagingPage(
      resourceGroupName,
      networkManagerName,
      workspaceName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets list of Reachability Analysis Runs.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    options?: ReachabilityAnalysisRunsListOptionalParams,
  ): Promise<ReachabilityAnalysisRunsListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, networkManagerName, workspaceName, options },
      listOperationSpec,
    );
  }

  /**
   * Gets Reachability Analysis Run.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    options?: ReachabilityAnalysisRunsGetOptionalParams,
  ): Promise<ReachabilityAnalysisRunsGetResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        networkManagerName,
        workspaceName,
        reachabilityAnalysisRunName,
        options,
      },
      getOperationSpec,
    );
  }

  /**
   * Creates Reachability Analysis Runs.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param body Analysis Run resource object to create/update.
   * @param options The options parameters.
   */
  create(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    body: ReachabilityAnalysisRun,
    options?: ReachabilityAnalysisRunsCreateOptionalParams,
  ): Promise<ReachabilityAnalysisRunsCreateResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        networkManagerName,
        workspaceName,
        reachabilityAnalysisRunName,
        body,
        options,
      },
      createOperationSpec,
    );
  }

  /**
   * Deletes Reachability Analysis Run.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    options?: ReachabilityAnalysisRunsDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ReachabilityAnalysisRunsDeleteResponse>,
      ReachabilityAnalysisRunsDeleteResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<ReachabilityAnalysisRunsDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        networkManagerName,
        workspaceName,
        reachabilityAnalysisRunName,
        options,
      },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      ReachabilityAnalysisRunsDeleteResponse,
      OperationState<ReachabilityAnalysisRunsDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes Reachability Analysis Run.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    options?: ReachabilityAnalysisRunsDeleteOptionalParams,
  ): Promise<ReachabilityAnalysisRunsDeleteResponse> {
    const poller = await this.beginDelete(
      resourceGroupName,
      networkManagerName,
      workspaceName,
      reachabilityAnalysisRunName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    nextLink: string,
    options?: ReachabilityAnalysisRunsListNextOptionalParams,
  ): Promise<ReachabilityAnalysisRunsListNextResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        networkManagerName,
        workspaceName,
        nextLink,
        options,
      },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/verifierWorkspaces/{workspaceName}/reachabilityAnalysisRuns",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ReachabilityAnalysisRunListResult,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.skipToken,
    Parameters.skip,
    Parameters.top1,
    Parameters.sortKey,
    Parameters.sortValue,
  ],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.workspaceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/verifierWorkspaces/{workspaceName}/reachabilityAnalysisRuns/{reachabilityAnalysisRunName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ReachabilityAnalysisRun,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.workspaceName,
    Parameters.reachabilityAnalysisRunName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/verifierWorkspaces/{workspaceName}/reachabilityAnalysisRuns/{reachabilityAnalysisRunName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.ReachabilityAnalysisRun,
    },
    201: {
      bodyMapper: Mappers.ReachabilityAnalysisRun,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  requestBody: Parameters.body4,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.workspaceName,
    Parameters.reachabilityAnalysisRunName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/verifierWorkspaces/{workspaceName}/reachabilityAnalysisRuns/{reachabilityAnalysisRunName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.ReachabilityAnalysisRunsDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.ReachabilityAnalysisRunsDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.ReachabilityAnalysisRunsDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.ReachabilityAnalysisRunsDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.workspaceName,
    Parameters.reachabilityAnalysisRunName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ReachabilityAnalysisRunListResult,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.networkManagerName1,
    Parameters.workspaceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
